import {
  FiShield,
  FiAperture,
  FiUsers,
  FiStar,
  FiZap,
  FiLock,
  FiShoppingCart,
  FiMessageCircle,
} from "react-icons/fi";
import { IconType } from "react-icons";
import { useTranslation } from "react-i18next";

export default function PremiumPerks() {
  const { t } = useTranslation();
  interface Data {
    icon: IconType;
    title: string;
    desc: string;
  }

  interface ComparisonItem {
    feature: string;
    free: string;
    premium: string;
    icon: IconType;
    highlight?: boolean;
  }

  let premiumFeatures: Data[] = [
    {
      icon: FiShield,
      title: t("premiumPerks.features.verifiedCommunity.title"),
      desc: t("premiumPerks.features.verifiedCommunity.description"),
    },
    {
      icon: FiAperture,
      title: t("premiumPerks.features.premiumModeration.title"),
      desc: t("premiumPerks.features.premiumModeration.description"),
    },
    {
      icon: FiUsers,
      title: t("premiumPerks.features.exclusiveNetwork.title"),
      desc: t("premiumPerks.features.exclusiveNetwork.description"),
    },
    {
      icon: FiStar,
      title: t("premiumPerks.features.unlimitedAccess.title"),
      desc: t("premiumPerks.features.unlimitedAccess.description"),
    },
  ];

  let comparisonData: ComparisonItem[] = [
    {
      feature: t("premiumPerks.comparison.features.readDiscussions"),
      free: t("premiumPerks.comparison.values.unlimited"),
      premium: t("premiumPerks.comparison.values.unlimited"),
      icon: FiMessageCircle,
    },
    {
      feature: t("premiumPerks.comparison.features.postLimit"),
      free: t("premiumPerks.comparison.values.upTo5Posts"),
      premium: t("premiumPerks.comparison.values.unlimitedPosts"),
      icon: FiUsers,
      highlight: true,
    },
    {
      feature: t("premiumPerks.comparison.features.aiTools"),
      free: t("premiumPerks.comparison.values.notIncluded"),
      premium: t("premiumPerks.comparison.values.unlimitedAccess"),
      icon: FiZap,
      highlight: true,
    },
    {
      feature: t("premiumPerks.comparison.features.privateMessaging"),
      free: t("premiumPerks.comparison.values.notIncluded"),
      premium: t("premiumPerks.comparison.values.unlimitedChats"),
      icon: FiLock,
      highlight: true,
    },
    {
      feature: t("premiumPerks.comparison.features.marketplaceAccess"),
      free: t("premiumPerks.comparison.values.notIncluded"),
      premium: t("premiumPerks.comparison.values.fullAccess"),
      icon: FiShoppingCart,
      highlight: true,
    },
    {
      feature: t("premiumPerks.comparison.features.earlyAccess"),
      free: t("premiumPerks.comparison.values.restricted"),
      premium: t("premiumPerks.comparison.values.priorityAccess"),
      icon: FiStar,
      highlight: true,
    },
  ];

  return (
    <div className="grid md:grid-cols-12 grid-cols-1 gap-6 sm:gap-8">
      {/* Left Side - Content */}
      <div className="md:col-span-6">
        <h6 className="text-red-500 uppercase text-xs sm:text-sm font-bold tracking-wider mb-2 sm:mb-3">
          {t("premiumPerks.badge")}
        </h6>
        <h4 className="mb-4 sm:mb-6 text-xl sm:text-2xl md:text-3xl md:leading-normal leading-normal font-bold">
          {t("premiumPerks.title")}
        </h4>
        <p className="text-slate-400 max-w-xl mb-6 sm:mb-8 text-sm sm:text-base">
          {t("premiumPerks.description")}{" "}
          <span className="text-green-600 font-semibold">
            {t("hero.price")}
          </span>{" "}
          {t("premiumPerks.priceText")}
        </p>

        {/* Premium Features Grid */}
        <div className="grid grid-cols-1 gap-3 sm:gap-4">
          {premiumFeatures.map((item: Data, index: number) => {
            let Icon = item.icon;
            return (
              <div
                className="group flex relative overflow-hidden p-3 sm:p-4 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-primaryColor dark:hover:bg-primaryColor duration-500"
                key={index}
              >
                <span className="text-red-500 group-hover:text-white text-5xl font-semibold duration-500 flex-shrink-0">
                  <Icon className="size-5 sm:size-6 mt-1" />
                </span>
                <div className="flex-1 ms-2 sm:ms-3">
                  <h5 className="group-hover:text-white text-sm sm:text-base font-semibold duration-500">
                    {item.title}
                  </h5>
                  <p className="text-slate-400 group-hover:text-white/70 duration-500 text-xs sm:text-sm mt-1 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Side - Enhanced Comparison Table */}
      <div className="md:col-span-6">
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {/* Enhanced Header */}
          <div className="bg-gradient-to-br from-primaryColor via-primaryColor/90 to-primaryColor/80 text-white p-4 sm:p-5 md:p-6 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white rounded-full -translate-y-12 translate-x-12 sm:-translate-y-16 sm:translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-16 h-16 sm:w-24 sm:h-24 bg-white rounded-full translate-y-8 -translate-x-8 sm:translate-y-12 sm:-translate-x-12"></div>
            </div>

            <div className="relative">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <h5 className="font-bold text-lg sm:text-xl md:text-2xl">
                  {t("premiumPerks.comparison.title")}
                </h5>
              </div>
              <p className="text-white/90 text-[11px] xs:text-xs sm:text-sm font-medium mb-3 sm:mb-4 leading-relaxed">
                {t("premiumPerks.comparison.subtitle")}
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-yellow-300">
                <span className="text-[10px] xs:text-xs sm:text-sm font-semibold">
                  {t("premiumPerks.comparison.limitedTimeOffer")}
                </span>
                <span className="text-[10px] xs:text-xs leading-tight">
                  {t("premiumPerks.comparison.offerDetails")}
                </span>
              </div>
            </div>
          </div>

          {/* Enhanced Table Body */}
          <div className="p-3 sm:p-4 md:p-6">
            {/* Mobile Header - Shown only on mobile */}
            <div className="sm:hidden grid grid-cols-2 gap-2 text-xs font-bold text-slate-700 dark:text-slate-300 mb-3 border-b-2 border-slate-200 dark:border-slate-700 pb-3">
              <div className="text-center text-slate-600 dark:text-slate-400">
                {t("premiumPerks.comparison.headers.freePlan")}
              </div>
              <div className="text-center text-primaryColor font-bold">
                {t("premiumPerks.comparison.headers.premium")}
              </div>
            </div>

            {/* Table Header - Hidden on mobile, shown on larger screens */}
            <div className="hidden sm:grid sm:grid-cols-3 gap-4 md:gap-6 text-sm font-bold text-slate-700 dark:text-slate-300 mb-4 md:mb-6 border-b-2 border-slate-200 dark:border-slate-700 pb-3 md:pb-4">
              <div className="text-base md:text-lg flex items-center gap-2">
                <FiStar className="text-primaryColor" />
                {t("premiumPerks.comparison.headers.feature")}
              </div>
              <div className="text-center text-base md:text-lg">
                {t("premiumPerks.comparison.headers.freePlan")}
              </div>
              <div className="text-center text-primaryColor font-bold text-base md:text-lg relative">
                {t("premiumPerks.comparison.headers.premium")}
              </div>
            </div>

            {/* Enhanced Table Rows */}
            <div className="space-y-2 sm:space-y-3">
              {comparisonData.map((item, index) => {
                let Icon = item.icon;
                return (
                  <div
                    key={index}
                    className={`${
                      item.highlight
                        ? "border-primaryColor/30 bg-primaryColor/5 dark:bg-primaryColor/10 hover:bg-primaryColor/10 dark:hover:bg-primaryColor/20"
                        : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/30"
                    } border rounded-lg transition-all duration-200`}
                  >
                    {/* Mobile Layout - Stacked */}
                    <div className="sm:hidden p-3">
                      {/* Feature Row */}
                      <div className="flex items-start gap-2 mb-2">
                        <div
                          className={`p-1.5 rounded-md flex-shrink-0 ${
                            item.highlight
                              ? "bg-primaryColor/10 text-primaryColor"
                              : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400"
                          }`}
                        >
                          <Icon className="size-3.5" />
                        </div>
                        <span className="font-medium text-xs sm:text-sm text-slate-800 dark:text-slate-200 leading-tight">
                          {item.feature}
                        </span>
                      </div>

                      {/* Comparison Row */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="text-center py-1.5 px-2 bg-slate-50 dark:bg-slate-800 rounded-md">
                          <span
                            className={`text-xs font-medium break-words ${
                              item.free ===
                              t("premiumPerks.comparison.values.unlimited")
                                ? "text-green-600 dark:text-green-400"
                                : "text-slate-500 dark:text-slate-400"
                            }`}
                          >
                            {item.free}
                          </span>
                        </div>
                        <div className="text-center py-1.5 px-2 bg-primaryColor/5 dark:bg-primaryColor/10 rounded-md">
                          <span
                            className={`text-xs font-bold break-words ${
                              item.highlight
                                ? "text-primaryColor"
                                : "text-slate-700 dark:text-slate-300"
                            }`}
                          >
                            {item.premium}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Desktop Layout - Grid */}
                    <div className="hidden sm:grid sm:grid-cols-3 gap-3 md:gap-4 items-center p-2 md:p-3">
                      {/* Feature Column */}
                      <div className="flex items-center gap-2">
                        <div
                          className={`p-1.5 rounded-md flex-shrink-0 ${
                            item.highlight
                              ? "bg-primaryColor/10 text-primaryColor"
                              : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400"
                          }`}
                        >
                          <Icon className="size-3.5" />
                        </div>
                        <span className="font-medium text-xs sm:text-sm text-slate-800 dark:text-slate-200">
                          {item.feature}
                        </span>
                      </div>

                      {/* Free Plan Column */}
                      <div className="text-center">
                        <span
                          className={`text-xs sm:text-sm font-medium ${
                            item.free ===
                            t("premiumPerks.comparison.values.unlimited")
                              ? "text-green-600 dark:text-green-400"
                              : "text-slate-500 dark:text-slate-400"
                          }`}
                        >
                          {item.free}
                        </span>
                      </div>

                      {/* Premium Column */}
                      <div className="text-center">
                        <span
                          className={`text-xs sm:text-sm font-bold ${
                            item.highlight
                              ? "text-primaryColor bg-primaryColor/10 px-2 py-1 rounded-md"
                              : "text-slate-700 dark:text-slate-300"
                          }`}
                        >
                          {item.premium}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Promotional Banner - Full Width */}
      <div className="col-span-full mt-6 sm:mt-8 -mx-4 sm:-mx-6 lg:-mx-8 xl:-mx-12">
        <div className="relative overflow-hidden bg-gradient-to-r from-red-50 via-red-100 to-red-50 dark:from-red-900/30 dark:via-red-800/20 dark:to-red-900/30 py-4 sm:py-5 md:py-6 rounded-xl">
          {/* Circular gradient background effect */}
          <div className="absolute inset-0 bg-gradient-radial from-red-200/40 via-transparent to-transparent animate-pulse-slow" />

          <div className="relative flex items-center justify-center px-2">
            <div className="animate-scroll-text whitespace-nowrap text-xs sm:text-sm md:text-base font-bold text-red-700 dark:text-red-300">
              <span className="inline-flex items-center gap-2 sm:gap-3 md:gap-4">
                <span className="font-extrabold text-red-800 dark:text-red-200">
                  {t("promotionalBanner.exclusiveOffer")}
                </span>
                <span className="font-semibold">
                  {t("promotionalBanner.offerText")}
                </span>
                <span className="font-extrabold text-red-800 dark:text-red-200 animate-pulse">
                  {t("promotionalBanner.callToAction")}
                </span>
              </span>
            </div>
          </div>

          {/* Glowing effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-red-400/10 via-transparent to-red-400/10 animate-pulse-slow" />
        </div>
      </div>
    </div>
  );
}
